import React, { FC, ReactNode, useState, JSX } from "react";
import {
  CheckIconSvg,
  CloseIconSvg,
  ErrorIconSvg,
  InfoIconSvg,
  LockIconSvg,
  WarningIconSvg,
} from "../../../components/icons";
import { HexColor } from "../../../types/styles";
import { colors } from "../../../styles/variables";
import {
  StyledAlert,
  StyledChildrenWrapper,
  StyledCloseIcon,
  StyledContent,
  StyledIcon,
  StyledText,
} from "./index.styled";

export type AlertTypes = "error" | "info" | "lock" | "success" | "warning";

type AlertPropertyMap = Record<
  AlertTypes,
  {
    textColor: HexColor;
    backgroundColor: HexColor;
    icon: JSX.Element;
  }
>;

export const alertPropertyMap: AlertPropertyMap = {
  error: {
    textColor: colors.red,
    backgroundColor: "#FAE5E6",
    icon: <ErrorIconSvg color={colors.red} height="100%" width="100%" />,
  },
  info: {
    textColor: colors.blue,
    backgroundColor: "#E6F5FE",
    icon: <InfoIconSvg color={colors.blue} height="100%" width="100%" />,
  },
  lock: {
    textColor: colors.blue,
    backgroundColor: "#E6F5FE",
    icon: <LockIconSvg color={colors.blue} height="100%" width="100%" />,
  },
  success: {
    textColor: colors.green,
    backgroundColor: "#E6F3EC",
    icon: <CheckIconSvg color={colors.green} height="100%" width="100%" />,
  },
  warning: {
    textColor: colors.brown,
    backgroundColor: "#FEF3E6",
    icon: <WarningIconSvg color={colors.brown} height="100%" width="100%" />,
  },
};

interface AlertProps {
  bold?: boolean;
  children: ReactNode | string;
  className?: string;
  closable?: boolean;
  large?: boolean;
  onClose?: () => void;
  small?: boolean;
  title?: string;
  type?: AlertTypes;
}

export const Alert: FC<AlertProps> = ({
  bold = false,
  children,
  className,
  closable = false,
  large = false,
  onClose,
  small = false,
  title,
  type = "info",
}) => {
  const [close, setClose] = useState(false);
  const { textColor, backgroundColor, icon } = alertPropertyMap[type];

  const handleClose = () => {
    if (onClose) onClose();
    setClose(true);
  };

  if (close) return null;

  return (
    <StyledAlert
      className={className || ""}
      color={backgroundColor}
      data-testid="alert"
      role="alert"
      small={small}
    >
      <StyledIcon small={small}>{icon}</StyledIcon>

      <StyledContent small={small} large={large}>
        {title && (
          <div>
            <StyledText
              bold
              closable={closable}
              color={textColor}
              gutterBottom
              isTitle
              large={large}
            >
              {title}
            </StyledText>
          </div>
        )}
        {typeof children === "string" ? (
          <StyledText
            color={textColor}
            bold={bold}
            closable={closable}
            large={large}
          >
            {children}
          </StyledText>
        ) : (
          <StyledChildrenWrapper color={textColor}>
            {children}
          </StyledChildrenWrapper>
        )}
      </StyledContent>

      {closable && (
        <StyledCloseIcon onClick={handleClose} role="button" small={small}>
          <CloseIconSvg color={textColor} height="100%" width="100%" />
        </StyledCloseIcon>
      )}
    </StyledAlert>
  );
};
